<template>
  <!-- Tabla jerárquica del PRM. Solo presentación: recibe el mapa ya calculado y emite eventos. -->
  <div class="prm-table-wrap">
  <!-- Barra de scroll horizontal superior, sincronizada con la tabla: permite desplazarse
       en horizontal sin bajar hasta el final para alcanzar la barra de abajo. -->
  <div ref="topScroll" class="prm-top-scroll" @scroll="$refs.scroll.scrollLeft = $refs.topScroll.scrollLeft">
    <div class="prm-top-rail" :style="{ width: railWidth + 'px' }"></div>
  </div>
  <!-- Arrastrar con el botón central de la rueda hace scroll (pan) en ambos ejes. -->
  <div ref="scroll" class="prm-scroll" :class="{ panning }" @mousedown.middle.prevent="startPan"
       @scroll="$refs.topScroll.scrollLeft = $refs.scroll.scrollLeft">
    <table class="prm-table" :class="{ 'is-empty': !rows.length }">
      <thead>
        <!-- Bandas de grupo -->
        <tr class="band">
          <th class="sticky-col-sec band-empty" rowspan="2"></th>
          <th class="sticky-col band-empty"></th>
          <th :colspan="3">{{ $t('prm_budget') }}</th>
          <th :colspan="months.length + 1" class="band-month">{{ $t('prm_reviewed_month') }}</th>
          <th :colspan="confirmedCos.length + 1" class="band-co">{{ $t('prm_co_approved') }}</th>
          <th :colspan="expectedCos.length + 1" class="band-co2">{{ $t('prm_co_pending') }}</th>
          <th :colspan="6" class="band-result">{{ $t('prm_result') }}</th>
          <th :colspan="2" class="band-deviation">{{ $t('prm_deviation') }}</th>
        </tr>
        <!-- Cabeceras de columna -->
        <tr class="head">
          <th class="sticky-col">{{ $t('prm_item') }}</th>
          <th class="num">{{ $t('prm_commercial_fit') }}</th>
          <th class="num">{{ $t('prm_pm_initial_fit') }}</th>
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_commercial_deviation') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_commercial_deviation')"></source-chip>
            </span>
          </th>
          <th v-for="m in months" :key="m.id" class="num col-dyn">
            <div class="col-head">
              <input class="col-label" :value="m.label" @change="$emit('rename-column', { id: m.id, label: $event.target.value })">
              <button class="col-x" :title="$t('prm_remove_month')" @click="$emit('remove-column', m.id)">×</button>
            </div>
          </th>
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_deviations') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_deviations')"></source-chip>
            </span>
          </th>
          <!-- CO Aprobadas (confirmadas) + su sumatoria -->
          <th v-for="c in confirmedCos" :key="c.id" class="num col-dyn">
            <div class="col-head">
              <input class="col-label" :value="c.label" @change="$emit('rename-column', { id: c.id, label: $event.target.value })">
              <button class="col-x" :title="$t('prm_remove_co')" @click="$emit('remove-column', c.id)">×</button>
            </div>
            <button class="co-status confirmed" @click="$emit('toggle-co', c.id)" :title="$t('prm_co_confirmed_hint')">
              <i class="fas fa-circle-check"></i> {{ $t('prm_co_confirmed') }}
            </button>
          </th>
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_total_co_received') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_total_co_received')"></source-chip>
            </span>
          </th>
          <!-- CO Pendientes (esperadas) + su sumatoria -->
          <th v-for="c in expectedCos" :key="c.id" class="num col-dyn co-expected">
            <div class="col-head">
              <input class="col-label" :value="c.label" @change="$emit('rename-column', { id: c.id, label: $event.target.value })">
              <button class="col-x" :title="$t('prm_remove_co')" @click="$emit('remove-column', c.id)">×</button>
            </div>
            <button class="co-status expected" @click="$emit('toggle-co', c.id)" :title="$t('prm_co_expected_hint')">
              <i class="fas fa-circle-question"></i> {{ $t('prm_co_expected') }}
            </button>
          </th>
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_total_co_expected') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_total_co_expected')"></source-chip>
            </span>
          </th>
          <!-- Resultado -->
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_total_estimate') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_total_estimate')"></source-chip>
            </span>
          </th>
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_pending_cost') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_pending_cost')"></source-chip>
            </span>
          </th>
          <th class="num">{{ $t('prm_accumulated') }}</th>
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_incurred_month') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_incurred_month')"></source-chip>
            </span>
          </th>
          <th class="num">{{ $t('prm_incurred_total') }}</th>
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_work_progress') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_work_pct')"></source-chip>
            </span>
          </th>
          <!-- Desviación: Inicial proyectos - Revisado + Observaciones -->
          <th class="num">
            <span class="th-in">
              <span>{{ $t('prm_initial_vs_revised') }}</span>
              <source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_initial_vs_revised')"></source-chip>
            </span>
          </th>
          <th class="obs-head">{{ $t('prm_observations') }}</th>
        </tr>
      </thead>

      <tbody>
        <!-- Estado vacío: aún no hay partidas; invita a importar una plantilla. -->
        <tr v-if="!rows.length" class="empty-row">
          <td :colspan="colCount" class="empty-state">{{ $t('prm_empty_hint') }}</td>
        </tr>

        <tr v-for="row in displayRows" :key="row.node.id" :class="rowClass(row.node)">
          <!-- Columna de sección: banda vertical (rotada) que engloba todas sus filas.
               En las filas ya englobadas (spanned) no se pinta; el rowspan la cubre. -->
          <td v-if="!row.sec.spanned" class="sticky-col-sec" :class="{ 'sec-empty': row.sec.empty }"
              :style="row.sec.color ? { background: row.sec.color } : null" :rowspan="row.sec.rowspan || 1">
            <span v-if="!row.sec.empty" class="sec-label">{{ row.sec.label }}</span>
          </td>
          <td class="sticky-col row-label" :style="labelStyle(row)">{{ row.node.label }}</td>
          <td v-for="col in columnPlan" :key="col.key"
              :class="[col.type === 'observaciones' ? 'obs-cell' : 'num', cellClass(row.node, col)]"
              @click="col.type !== 'observaciones' && editable(row.node, col) && startEdit(row.node.id, col.key)">
            <!-- Observaciones: texto de la hoja, solo lectura (viene del Excel importado). -->
            <template v-if="col.type === 'observaciones'">
              <span v-if="isLeaf(row.node)" class="obs-text">{{ obsVal(row.node.id) }}</span>
            </template>
            <template v-else>
              <i v-if="editable(row.node, col)" class="fas fa-comment-dots note-icon"
                 :class="{ 'has-note': hasNote(row.node, col) }" :title="''"
                 @click.stop="openNote(row.node, col, $event)"
                 @mouseenter="onIconEnter(row.node, col, $event)" @mouseleave="hideTip"></i>
              <input v-if="isEditing(row.node.id, col.key)" ref="cellInput" type="number" class="cell-input"
                     :value="editVal(row.node.id, col)"
                     @blur="commit(row.node.id, col, $event.target.value)"
                     @keydown.enter="commit(row.node.id, col, $event.target.value)"
                     @keydown.esc="editing = {}">
              <span v-else :class="{ neg: isNeg(row.node.id, col), empty: isEmpty(row.node.id, col) }">{{ display(row.node, col) }}</span>
              <!-- Fuente de datos: celda de fila padre (grupo/sección) que suma las de sus hijas. -->
              <source-chip v-if="mode && parentSumCell(row.node, col)" source="calc" :tip-override="$t('prm_src_formula_children_sum')"></source-chip>
            </template>
          </td>
        </tr>

        <!-- Filas de cierre: solo cuando hay datos. -->
        <template v-if="rows.length">
        <!-- TOTAL COSTES -->
        <tr class="total">
          <td class="sticky-col-sec"></td>
          <td class="sticky-col row-label">{{ $t('prm_total_costs') }}</td>
          <td v-for="col in columnPlan" :key="col.key" class="num">
            <span :class="{ neg: totalNeg(col), empty: totalRaw(col) == null }">{{ totalDisplay(col) }}</span>
          </td>
        </tr>

        <!-- MARGEN (proyecto): comercial bajo Comercial, inicial PM bajo Inicial. -->
        <tr class="margin-row">
          <td class="sticky-col-sec"></td>
          <td class="sticky-col row-label">{{ $t('prm_margin') }}<source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_margin')"></source-chip></td>
          <td v-for="col in columnPlan" :key="col.key" class="num">
            <template v-if="col.type === 'ec'">{{ eur(totals.project.marginEc) }}</template>
            <template v-else-if="col.type === 'ep'">{{ eur(totals.project.marginEp) }}</template>
          </td>
        </tr>

        <!-- % MARGEN (proyecto) -->
        <tr class="margin-pct-row">
          <td class="sticky-col-sec"></td>
          <td class="sticky-col row-label">% {{ $t('prm_margin') }}<source-chip v-if="mode" source="calc" :tip-override="$t('prm_src_formula_margin_pct')"></source-chip></td>
          <td v-for="col in columnPlan" :key="col.key" class="num">
            <span v-if="col.type === 'ec'" :class="signCls(totals.project.marginPctEc)">{{ pct(totals.project.marginPctEc) }}</span>
            <span v-else-if="col.type === 'ep'" :class="signCls(totals.project.marginPctEp)">{{ pct(totals.project.marginPctEp) }}</span>
          </td>
        </tr>
        </template>
      </tbody>
    </table>
  </div>

    <!-- Editor de nota (mini popover) + tooltip, compartidos y posicionados al vuelo. -->
    <div v-if="notePop.open" class="note-backdrop" @click="closeNote"></div>
    <div v-if="notePop.open" class="note-pop" :style="notePop.style" @mousedown.stop>
      <textarea ref="noteText" v-model="notePop.text" class="note-text" rows="3"
                :placeholder="$t('prm_note_placeholder')" @keydown.esc="closeNote"></textarea>
      <div class="note-actions">
        <button class="note-btn" @click="closeNote">{{ $t('prm_note_cancel') }}</button>
        <button class="note-btn save" @click="saveNote">{{ $t('prm_note_save') }}</button>
      </div>
    </div>
    <div v-show="tip.show" class="cell-tip" :style="tip.style">{{ tip.text }}</div>
  </div>
</template>

<script>
  import { fmtEur, fmtNum, fmtPct } from '../shared/format'
  import SourceChip from '../summary/source-chip.vue'

  export default {
    name: 'PrmTemplateTable',

    components: { SourceChip },

    props: {
      rows: { type: Array, required: true },       // [{ node, depth }]
      months: { type: Array, required: true },
      cos: { type: Array, required: true },
      computedMap: { type: Object, required: true },
      totals: { type: Object, required: true },
      edited: { type: Object, default: () => ({}) }, // celdas cambiadas en la sesión
      notes: { type: Object, default: () => ({}) },  // notas por celda (leafId::colKey → texto)
      observations: { type: Object, default: () => ({}) }, // observaciones por hoja (leafId → texto)
      mode: { type: Boolean, default: false },        // modo "Fuente de datos": marca las columnas calculadas
    },

    data: () => ({
      editing: {}, // { row, key } de la celda en edición
      panning: false, // arrastre con el botón central activo
      pan: null,      // estado del arrastre (origen y scroll inicial)
      railWidth: 0, // ancho (px) del riel de la barra superior (= ancho real de la tabla)
      notePop: { open: false, node: null, col: null, text: '', style: {} }, // editor de nota
      tip: { show: false, text: '', style: {} },                             // tooltip de nota
    }),

    mounted () {
      // El riel de la barra superior debe medir lo mismo que la tabla; observamos su
      // tamaño para mantenerlo al día cuando cambian columnas, filas o anchos.
      const el = this.$refs.scroll
      this.ro = new ResizeObserver(() => { this.railWidth = el.scrollWidth })
      this.ro.observe(el.firstElementChild)
      this.railWidth = el.scrollWidth
    },

    beforeDestroy () {
      this.endPan()
      if (this.ro) this.ro.disconnect()
    },

    computed: {
      // COs agrupadas por estado: aprobadas (confirmadas) y pendientes (esperadas).
      // Cada grupo se pinta con su propia sumatoria, igual que en la plantilla nueva.
      confirmedCos () {
        return this.cos.filter((c) => (c.status || 'confirmed') !== 'expected')
      },
      expectedCos () {
        return this.cos.filter((c) => (c.status || 'confirmed') === 'expected')
      },
      // Totales de las columnas CO en el pie: coste (todas las partidas) + la fila de Ventas
      // (VENTAS / POC), que en el resto de totales no suma pero en las Change Orders sí.
      // Preserva el vacío (rayita) si ni coste ni ventas tienen dato.
      footerCos () {
        const cost = (this.totals.cost && this.totals.cost.cos) || {}
        const sales = (this.totals.sales && this.totals.sales.cos) || {}
        const out = {}
        this.cos.forEach((c) => {
          const a = cost[c.id]
          const b = sales[c.id]
          out[c.id] = (a == null && b == null) ? null : (a || 0) + (b || 0)
        })
        return out
      },
      // Plan ordenado de columnas de valor (editables + calculadas).
      columnPlan () {
        const plan = [
          { key: 'ec', type: 'ec' },
          { key: 'ep', type: 'ep' },
          // Desv. Plan Comercial = encaje comercial − inicial (calculada, no se importa).
          { key: 'commercialDeviation', type: 'commercialDeviation', calc: true },
        ]
        this.months.forEach((m) => plan.push({ key: 'm:' + m.id, type: 'month', id: m.id }))
        plan.push({ key: 'deviations', type: 'deviations', calc: true })
        // Change Orders en dos grupos (aprobadas / pendientes), cada uno con su sumatoria.
        this.confirmedCos.forEach((c) => plan.push({ key: 'co:' + c.id, type: 'co', id: c.id }))
        plan.push({ key: 'totalCoConfirmed', type: 'totalCoConfirmed', calc: true })
        this.expectedCos.forEach((c) => plan.push({ key: 'co:' + c.id, type: 'co', id: c.id }))
        plan.push({ key: 'totalCoExpected', type: 'totalCoExpected', calc: true })
        // Coste Total Estimado (calculada: inicial + desviaciones + CO aprobadas).
        plan.push({ key: 'totalEstimate', type: 'totalEstimate', calc: true })
        // Área de resultados: columnas de obra en curso de la plantilla. Manuales:
        // Acumulado e Incurrido Total; el resto (Incurrido Mes, Costes ptes, Obra %) se calcula.
        plan.push({ key: 'pendingCost', type: 'pendingCost', calc: true })
        plan.push({ key: 'accumulated', type: 'accumulated' })
        plan.push({ key: 'incurredMonth', type: 'incurredMonth', calc: true })
        plan.push({ key: 'incurredTotal', type: 'incurredTotal' })
        plan.push({ key: 'workPct', type: 'workPct', calc: true })
        // Sección Desviación: Inicial proyectos - Revisado (calculada) + Observaciones (texto).
        plan.push({ key: 'deviationInitial', type: 'deviationInitial', calc: true })
        plan.push({ key: 'obs', type: 'observaciones' })
        return plan
      },
      // Nº de columnas de la tabla (sección + partida fija + plan) para el colspan del estado vacío.
      colCount () {
        return 2 + this.columnPlan.length
      },
      // Filas a pintar. Las secciones (p. ej. MANUFACTURING) NO son una fila: se
      // convierten en una banda vertical a la izquierda que engloba (rowspan) todas
      // sus filas hijas, como el merge de la columna A del Excel.
      displayRows () {
        const src = this.rows
        const out = []
        let i = 0
        while (i < src.length) {
          const { node, depth } = src[i]
          if (node.role === 'section') {
            // Descendientes contiguos (grupos + hojas) con mayor profundidad.
            const desc = []
            let j = i + 1
            while (j < src.length && src[j].depth > depth) { desc.push(src[j]); j++ }
            if (desc.length) {
              desc.forEach((r, k) => {
                out.push({
                  node: r.node,
                  depth: r.depth - depth - 1, // relativa a la sección (grupo a nivel 0)
                  sec: k === 0 ? { label: node.label, rowspan: desc.length, color: node.color } : { spanned: true },
                })
              })
            } else {
              // Sección sin hijas: se pinta como una sola fila con su propia banda.
              out.push({ node, depth: 0, sec: { label: node.label, rowspan: 1, color: node.color } })
            }
            i = j
          } else {
            out.push({ node, depth, sec: { empty: true } })
            i++
          }
        }
        return out
      },
    },

    methods: {
      isLeaf (node) {
        return node.role === 'leaf' || node.role === 'sales'
      },
      // Solo son editables en el sistema las columnas de Revisado mes y las Change Orders.
      // El resto (Encaje Comercial/Inicial, Acumulado, Incurrido Total y las calculadas) viene
      // como está del Excel importado y no se puede editar aquí.
      editable (node, col) {
        return this.isLeaf(node) && ['month', 'co'].includes(col.type)
      },
      // Celda de una fila padre (grupo/sección) cuyo valor es la SUMA de sus hijas: en las
      // columnas que a nivel de hoja son manuales, en un padre pasan a ser calculadas.
      parentSumCell (node, col) {
        return !!(node.children && node.children.length) &&
          ['ec', 'ep', 'month', 'co', 'accumulated', 'incurredTotal'].includes(col.type)
      },
      rowClass (node) {
        // row-info: filas "Nº de horas" (informativas), atenuadas y fuera de los totales.
        return ['row-' + node.role, { 'row-info': node.info }]
      },
      // Estilo de la celda de nombre: solo la sangría por profundidad.
      labelStyle (row) {
        return { paddingLeft: (10 + row.depth * 14) + 'px' }
      },
      // Observaciones (texto de la hoja, solo lectura).
      obsVal (nodeId) {
        return this.observations[nodeId] || ''
      },
      // Valor crudo de una celda: number, o null/undefined si está vacía (sin dato).
      rawVal (nodeId, col) {
        const c = this.computedMap[nodeId] || {}
        switch (col.type) {
          case 'ec': return c.ec
          case 'ep': return c.ep
          case 'commercialDeviation': return c.commercialDeviation
          case 'month': return (c.months || {})[col.id]
          case 'deviations': return c.deviations
          case 'co': return (c.cos || {})[col.id]
          case 'totalCoConfirmed': return this.sumCos(c.cos, this.confirmedCos)
          case 'totalCoExpected': return this.sumCos(c.cos, this.expectedCos)
          case 'totalCo': return c.totalCo
          case 'totalEstimate': return c.totalEstimate
          case 'deviationInitial': return c.deviationInitial
          case 'margin': return c.margin
          case 'marginPct': return c.marginPct
          case 'pendingCost': return c.pendingCost
          case 'accumulated': return c.accumulated
          case 'incurredMonth': return c.incurredMonth
          case 'incurredTotal': return c.incurredTotal
          case 'workPct': return c.workPct
        }
      },
      // Suma (preservando el vacío) los valores de una lista de COs de un mapa co.
      sumCos (cosMap, list) {
        cosMap = cosMap || {}
        let sum = null
        list.forEach((c) => {
          const v = cosMap[c.id]
          if (v !== null && v !== undefined) sum = (sum || 0) + v
        })
        return sum
      },
      // Una celda está vacía (rayita) si no tiene valor. El 0 SÍ es valor.
      isEmpty (nodeId, col) {
        const v = this.rawVal(nodeId, col)
        return v === null || v === undefined
      },
      // Precargamos el input con el valor (incluido 0); vacío solo si no hay dato.
      editVal (nodeId, col) {
        const v = this.rawVal(nodeId, col)
        return (v === null || v === undefined) ? '' : v
      },
      // Vacío → rayita; el 0 y cualquier número (calculado o no) se muestran.
      // Las filas informativas "Nº de horas" llevan números sueltos, no euros.
      display (node, col) {
        const v = this.rawVal(node.id, col)
        if (v === null || v === undefined) return '—'
        if (col.type === 'marginPct' || col.type === 'workPct') return fmtPct(v)
        if (node.info) return fmtNum(v)
        return fmtEur(v)
      },
      isNeg (nodeId, col) {
        const v = this.rawVal(nodeId, col)
        return v != null && v < 0 && ['deviations', 'commercialDeviation', 'deviationInitial', 'margin', 'marginPct', 'pendingCost', 'incurredMonth'].includes(col.type)
      },
      cellClass (node, col) {
        return {
          editable: this.editable(node, col),
          calc: col.calc,
          'col-dyn': col.type === 'month' || col.type === 'co',
          'is-edited': this.edited[node.id + '::' + col.key],
        }
      },
      isEditing (row, key) {
        return this.editing.row === row && this.editing.key === key
      },
      startEdit (row, key) {
        this.editing = { row, key }
        this.$nextTick(() => {
          const el = this.$refs.cellInput && this.$refs.cellInput[0]
          if (el) el.focus()
        })
      },
      commit (nodeId, col, raw) {
        this.editing = {}
        const s = String(raw).trim()
        const value = s === '' ? null : Number(s) // vacío = null (sin dato); "0" = valor 0
        if (value !== null && !Number.isFinite(value)) return
        // Solo emitimos si cambió: vacío→vacío o el mismo número no llaman al API.
        const cur = this.rawVal(nodeId, col)
        const curEmpty = cur === null || cur === undefined
        if (value === null && curEmpty) return
        if (value !== null && !curEmpty && value === cur) return
        this.$emit('edit-cell', { leafId: nodeId, field: this.fieldOf(col), colId: col.id || null, value, key: nodeId + '::' + col.key })
      },

      // Campo de persistencia de una columna editable (ec/ep/m/co/it/ac).
      fieldOf (col) {
        return { ec: 'ec', ep: 'ep', month: 'm', co: 'co', accumulated: 'ac', incurredTotal: 'it' }[col.type]
      },

      // ── Notas / descripción por celda ──
      // La nota se guarda bajo su campo (ec/ep/it/ac, o m:colId/co:colId), NO bajo
      // col.key: derivamos la clave igual que la persistencia para que la búsqueda cuadre
      // (col.key de las columnas nuevas es 'accumulated'/'incurredTotal', no 'ac'/'it').
      noteKey (node, col) {
        const field = this.fieldOf(col)
        return node.id + '::' + ((col.type === 'month' || col.type === 'co') ? field + ':' + col.id : field)
      },
      hasNote (node, col) {
        return !!this.notes[this.noteKey(node, col)]
      },
      openNote (node, col, e) {
        this.hideTip()
        this.notePop = {
          open: true, node, col,
          text: this.notes[this.noteKey(node, col)] || '',
          style: this.popStyle(e.currentTarget),
        }
        this.$nextTick(() => { if (this.$refs.noteText) this.$refs.noteText.focus() })
      },
      saveNote () {
        const { node, col, text } = this.notePop
        this.$emit('edit-note', { leafId: node.id, field: this.fieldOf(col), colId: col.id || null, note: (text || '').trim(), key: this.noteKey(node, col) })
        this.closeNote()
      },
      closeNote () {
        this.notePop = { open: false, node: null, col: null, text: '', style: {} }
      },
      onIconEnter (node, col, e) {
        if (this.notePop.open || !this.hasNote(node, col)) return
        this.tip = { show: true, text: this.notes[this.noteKey(node, col)], style: this.tipStyle(e.currentTarget) }
      },
      hideTip () {
        this.tip.show = false
      },
      // Posición fija bajo el icono, ajustada para no salirse por la derecha.
      popStyle (el) {
        const r = el.getBoundingClientRect()
        const w = 240
        const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8))
        return { top: (r.bottom + 6) + 'px', left: left + 'px', width: w + 'px' }
      },
      tipStyle (el) {
        const r = el.getBoundingClientRect()
        const left = Math.max(8, Math.min(r.left, window.innerWidth - 228))
        return { top: (r.bottom + 6) + 'px', left: left + 'px' }
      },
      // Totales de costes por columna (number, o null/undefined si la columna está vacía).
      totalRaw (col) {
        const c = this.totals.cost
        switch (col.type) {
          case 'ec': return c.ec
          case 'ep': return c.ep
          case 'commercialDeviation': return c.commercialDeviation
          case 'month': return (c.months || {})[col.id]
          case 'deviations': return c.deviations
          // Las columnas CO del pie suman también la fila de Ventas (footerCos).
          case 'co': return this.footerCos[col.id]
          case 'totalCoConfirmed': return this.sumCos(this.footerCos, this.confirmedCos)
          case 'totalCoExpected': return this.sumCos(this.footerCos, this.expectedCos)
          case 'totalCo': return c.totalCo
          case 'totalEstimate': return c.totalEstimate
          case 'deviationInitial': return c.deviationInitial
          case 'margin': return c.margin
          case 'marginPct': return c.marginPct
          case 'pendingCost': return c.pendingCost
          case 'accumulated': return c.accumulated
          case 'incurredMonth': return c.incurredMonth
          case 'incurredTotal': return c.incurredTotal
          case 'workPct': return c.workPct
        }
      },
      // Vacío → rayita; el 0 y cualquier número se muestran.
      totalDisplay (col) {
        if (col.type === 'observaciones') return '' // observaciones no tiene total
        const v = this.totalRaw(col)
        if (v === null || v === undefined) return '—'
        if (col.type === 'marginPct' || col.type === 'workPct') return fmtPct(v)
        return fmtEur(v)
      },
      totalNeg (col) {
        const v = this.totalRaw(col)
        return v != null && v < 0 && ['deviations', 'commercialDeviation', 'deviationInitial', 'margin', 'marginPct', 'pendingCost', 'incurredMonth'].includes(col.type)
      },
      signCls (v) {
        return v < 0 ? 'neg' : 'pos'
      },

      // Pan con el botón central: mueve solo el propio contenedor de la tabla
      // (nunca la página) en ambos ejes según su scroll disponible.
      startPan (e) {
        this.hideTip()
        const el = this.$refs.scroll
        this.pan = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop }
        this.panning = true
        window.addEventListener('mousemove', this.onPan)
        window.addEventListener('mouseup', this.endPan)
      },
      onPan (e) {
        if (!this.pan) return
        const el = this.$refs.scroll
        el.scrollLeft = this.pan.left - (e.clientX - this.pan.x)
        el.scrollTop = this.pan.top - (e.clientY - this.pan.y)
      },
      endPan () {
        if (!this.panning) return
        this.panning = false
        this.pan = null
        window.removeEventListener('mousemove', this.onPan)
        window.removeEventListener('mouseup', this.endPan)
      },

      eur: fmtEur,
      pct: fmtPct,
    },
  }
</script>

<style lang="scss" scoped>
  // min-width:0 permite que este contenedor encoja y la tabla haga scroll interno
  // en vez de estirar el ancho de toda la vista.
  .prm-table-wrap { min-width: 0; }
  // Barra de scroll horizontal superior: solo desplaza en horizontal; su riel interior
  // replica el ancho de la tabla. Bordes laterales y superior para integrarse con ella.
  .prm-top-scroll { min-width: 0; overflow-x: auto; overflow-y: hidden; border: 1px solid #d4dae3; border-bottom: 0; border-radius: 8px 8px 0 0; background: #eef1f5; }
  .prm-top-rail { height: 1px; }
  // Scroll en ambos ejes con altura acotada: así la cabecera (sticky top) y la columna
  // lateral (sticky left) quedan fijas dentro del propio contenedor al desplazarse.
  .prm-scroll { min-width: 0; overflow: auto; max-height: calc(100vh - 210px); border: 1px solid #d4dae3; border-radius: 0 0 8px 8px; }

  // Estilo de las barras de scroll (superior, horizontal y vertical): finas, con pulgar
  // redondeado en gris azulado y una separación con el carril para que se vean limpias.
  .prm-top-scroll, .prm-scroll { scrollbar-width: thin; scrollbar-color: #b7c2d0 #eef1f5; }
  .prm-top-scroll::-webkit-scrollbar, .prm-scroll::-webkit-scrollbar { width: 12px; height: 12px; }
  .prm-top-scroll::-webkit-scrollbar-track, .prm-scroll::-webkit-scrollbar-track { background: #eef1f5; }
  .prm-top-scroll::-webkit-scrollbar-thumb, .prm-scroll::-webkit-scrollbar-thumb { background: #b7c2d0; border-radius: 8px; border: 3px solid #eef1f5; }
  .prm-top-scroll::-webkit-scrollbar-thumb:hover, .prm-scroll::-webkit-scrollbar-thumb:hover { background: #8a99ac; }
  .prm-scroll::-webkit-scrollbar-corner { background: #eef1f5; }
  // Durante el arrastre con el botón central: cursor de manito y sin selección.
  // El `*` fuerza la manito también sobre inputs (que si no muestran el cursor de texto).
  .prm-scroll.panning { cursor: grabbing; user-select: none; }
  .prm-scroll.panning * { cursor: grabbing !important; }

  // separate (no collapse): imprescindible para que los bordes acompañen a la
  // columna fija (con collapse los bordes se desalinean y dejan líneas blancas).
  .prm-table {
    border-collapse: separate;
    border-spacing: 0;
    font-size: 11px;
    white-space: nowrap;
    color: #1e3046;
  }

  th, td { border-left: 1px solid #e6e9ee; border-bottom: 1px solid #eef0f3; padding: 5px 8px; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .neg { color: #c0392b; }
  .pos { color: #1e7a3d; }
  // Rayita de celda vacía: muy suave, para que no compita visualmente con los datos.
  .empty { color: #c8cfd8; }

  // Columna de sección (banda vertical tipo MANUFACTURING): fija a la izquierda del
  // todo, estrecha, con la etiqueta rotada. Engloba (rowspan) todas sus filas.
  .sticky-col-sec {
    position: sticky; left: 0; z-index: 3; width: 34px; min-width: 34px; max-width: 34px;
    padding: 2px; text-align: center; vertical-align: middle;
    background: #e9edf3; box-shadow: 1px 0 0 #d4dae3;
  }
  // Esquina superior izquierda (columna de sección + cabecera): fija en ambos ejes, por encima de todo.
  thead .band .sticky-col-sec { top: 0; z-index: 7; }
  // Filas sin sección (ventas, grupos de primer nivel): columna igual de gris, sin etiqueta.
  .sticky-col-sec.sec-empty { background: #e9edf3; }
  .sec-label {
    writing-mode: vertical-rl; transform: rotate(180deg); display: inline-block;
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
    color: #1e3046; white-space: nowrap;
  }
  // Los cierres tiñen su celda de sección igual que la fila, para no dejar hueco blanco.
  .total .sticky-col-sec { background: #e3e8ef; }
  .margin-row .sticky-col-sec { background: #1e3046; }
  .margin-pct-row .sticky-col-sec { background: #26374d; }

  // Columna fija de partida: opaca + separador, desplazada tras la columna de sección.
  .sticky-col {
    position: sticky; left: 34px; z-index: 3; min-width: 240px;
    text-align: left; font-size: 10px; background: #fff;
    box-shadow: 1px 0 0 #d4dae3;
  }
  // Columna de partida en la cabecera: fija también arriba (esquina, ambos ejes).
  thead .band .sticky-col { top: 0; z-index: 7; }
  thead .head .sticky-col { top: 21px; z-index: 7; }
  // El separador derecho de la columna fija se tiñe según la fila, para no dejar
  // una línea blanca sobre las cabeceras y pies oscuros.
  thead .head .sticky-col { box-shadow: 1px 0 0 #47597094; }
  .total .sticky-col { box-shadow: 1px 0 0 #cdd5df; }
  .margin-row .sticky-col, .margin-pct-row .sticky-col { box-shadow: 1px 0 0 #3a4c63; }

  // Columnas dinámicas (meses y COs): un ancho mínimo cómodo para que no se
  // apelotonen ni se corte el nombre.
  .col-dyn { min-width: 88px; }

  // Cabecera con distintivo de fuente (modo "Fuente de datos"): etiqueta + chip apilados,
  // alineados a la derecha como el resto de columnas numéricas.
  .th-in { display: inline-flex; flex-direction: column; align-items: flex-end; gap: 2px; }
  .th-in ::v-deep .src-chip { margin-left: 0; }

  // Cabeceras. Los separadores de columna van en tonos suaves (translúcido sobre
  // el fondo oscuro, gris claro sobre las bandas) para no marcar líneas blancas.
  thead .band th { position: sticky; top: 0; z-index: 5; line-height: 15px; background: #e9edf3; color: #33475f; font-weight: 600; text-align: center; text-transform: uppercase; letter-spacing: .03em; padding: 3px 8px; border-left-color: #cdd5df; }
  thead .band .band-month { background: #f4f2e6; }
  thead .band .band-co { background: #f5eee6; }
  thead .band .band-co2 { background: #f0e7dc; }
  thead .band .band-result { background: #e2efe4; }
  thead .band .band-deviation { background: #e9e6f1; }
  thead .band .band-empty { background: #e9edf3; }

  // Centro vertical: las cabeceras de CO tienen 2 líneas (nombre + estado), así el
  // resto de cabeceras de una línea quedan alineadas con ellas.
  thead .head th { position: sticky; top: 21px; z-index: 5; background: #33475f; color: #fff; font-weight: 600; vertical-align: middle; border-left-color: #47597094; }
  thead .head .sticky-col { background: #33475f; }
  .col-head { display: flex; align-items: center; gap: 2px; }
  .col-label { width: 100%; background: transparent; border: 0; border-bottom: 1px solid transparent; color: #fff; text-align: right; font-size: 10px; font-weight: 600; outline: none; }
  .col-label:focus { border-bottom-color: rgba(255, 255, 255, .5); }
  .col-x { background: transparent; border: 0; color: rgba(255, 255, 255, .5); cursor: pointer; font-size: 13px; line-height: 1; }
  .col-x:hover { color: #fff; }
  .co-expected { background: #4a4030; }
  .co-status { display: inline-flex; align-items: center; gap: 3px; margin-top: 3px; border: 0; border-radius: 3px; padding: 1px 6px; font-size: 9px; font-weight: 700; text-transform: uppercase; cursor: pointer; }
  .co-status i { font-size: 8px; }
  .co-status.confirmed { background: #2e7d5b; color: #fff; }
  .co-status.expected { background: #f0b429; color: #3a2f10; }

  // Estado vacío: la tabla ocupa todo el ancho para que la fila no quede cortada.
  .prm-table.is-empty { width: 100%; }
  .empty-row td.empty-state { text-align: center; padding: 28px 16px; color: #9aa4b2; font-size: 12px; background: #fff; }

  // Filas por rol
  .row-sales .row-label { background: #eef3fb; font-weight: 700; }
  .row-section .row-label { background: #e9edf3; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #1e3046; }
  .row-group .row-label { background: #f4f6f9; font-weight: 600; color: #33475f; }
  .row-leaf .row-label { background: #fafbfc; color: #4a5a6e; }
  // Filas informativas (Nº de horas): en cursiva y atenuadas; no suman en los totales.
  .row-info .row-label { font-style: italic; color: #8794a5; font-weight: 400; }
  .row-info td span { color: #97a2b1; font-style: italic; }

  // Columna de Observaciones (texto libre): alineada a la izquierda, editable en línea.
  .obs-head { text-align: left; min-width: 170px; }
  // Observaciones de solo lectura: tono neutro (no crema de editable) y texto que ajusta.
  td.obs-cell { text-align: left; min-width: 170px; max-width: 280px; white-space: normal; background: #f7f9fb; }
  .obs-text { font-size: 11px; color: #33475f; line-height: 1.35; }

  // Celdas de valor
  td.calc { background: #f4f6f9; font-weight: 500; }
  .row-section td, .row-group td { background: #f4f6f9; font-weight: 600; }
  .row-sales td { background: #eef3fb; }
  td.editable { background: #fffdf5; cursor: text; }
  // Celdas cambiadas en la sesión: amarillo suave (como el badge del contador).
  td.is-edited { background: #fff3cd; }

  // Icono de nota/descripción a la izquierda de las celdas editables. Aparece tenue
  // al pasar por la celda; si ya hay nota, queda ámbar y siempre visible.
  td.editable { position: relative; }
  .note-icon {
    position: absolute; left: 3px; top: 50%; transform: translateY(-50%);
    font-size: 9px; line-height: 1; color: #c3ccd8; opacity: 0; cursor: pointer;
    transition: opacity .1s, color .1s;
  }
  td.editable:hover .note-icon { opacity: 1; }
  .note-icon.has-note { opacity: 1; color: #d4a017; }
  .note-icon:hover { color: #33475f; }

  // Editor de nota (popover) y tooltip: posición fija, por encima de todo.
  .note-backdrop { position: fixed; inset: 0; z-index: 60; }
  .note-pop {
    position: fixed; z-index: 61; background: #fff; border: 1px solid #cbd5e0;
    border-radius: 8px; box-shadow: 0 6px 24px rgba(30, 48, 70, .18); padding: 10px;
  }
  .note-pop .note-text {
    width: 100%; box-sizing: border-box; border: 1px solid #cbd5e0; border-radius: 6px;
    padding: 6px 8px; font-size: 11px; color: #33475f; resize: vertical; outline: none;
  }
  .note-pop .note-text:focus { border-color: #9fb0c4; }
  .note-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 8px; }
  .note-btn {
    border: 1px solid #c3ccd8; border-radius: 4px; padding: 3px 10px;
    font-size: 11px; cursor: pointer; background: #fff; color: #33475f;
  }
  .note-btn.save { background: #33475f; color: #fff; border-color: #33475f; }
  .note-btn:hover { filter: brightness(0.97); }

  .cell-tip {
    position: fixed; z-index: 61; max-width: 220px; padding: 6px 9px; border-radius: 6px;
    background: #1e3046; color: #fff; font-size: 11px; line-height: 1.4; white-space: pre-wrap;
    box-shadow: 0 4px 14px rgba(30, 48, 70, .25); pointer-events: none;
  }
  .cell-input { width: 100%; text-align: right; font-size: 11px; padding: 0 2px; border: 1px solid #b7c2d0; border-radius: 3px; outline: none; }

  // Filas de totales
  // Filas oscuras de cierre: sin borde inferior claro (no está en el diseño).
  .total td { background: #e3e8ef; font-weight: 700; border-top: 2px solid #33475f; border-bottom: 0; border-left-color: #cdd5df; }
  .margin-row td { background: #1e3046; color: #fff; font-weight: 700; border-bottom: 0; border-left-color: #3a4c63; }
  .margin-pct-row td { background: #26374d; color: #fff; font-weight: 700; border-bottom: 0; border-left-color: #3a4c63; }
  .margin-pct-row .neg { color: #ff8a80; }
  .margin-pct-row .pos { color: #a5e6b8; }
</style>
